import { getAutocompleteQuerySuggestions } from "helpers/api";
import { getAutocompleteEndpointParams } from "helpers/endpointQueryParams";
import throttle from "lodash/throttle";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

const WAIT_BETWEEN_AUTOSUGGEST_REQUESTS_MSEC = 1000;

interface UseSearchSuggestionsReturn {
  suggestions: string[];
  selectedItemIndex: number;
  setSelectedItemIndex: (index: number) => void;
  onSuggestionsRequested: (request: { value: string }) => void;
  onSuggestionsCleared: () => void;
  handleKeyboardNavigation: (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    onSubmit: () => void
  ) => void;
}

export const useSearchSuggestions = (): UseSearchSuggestionsReturn => {
  const router = useRouter();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);

  const onSuggestionsRequested = useMemo(() => {
    const loadSuggestions = async ({
      value,
    }: {
      value: string;
    }): Promise<void> => {
      let newSuggestions: string[] = [];
      try {
        const params = getAutocompleteEndpointParams(router.query);
        const result = await getAutocompleteQuerySuggestions(value, params);
        newSuggestions = result.queries;
      } catch (error) {
        // do nothing
      }
      setSuggestions(newSuggestions);
    };
    return throttle(loadSuggestions, WAIT_BETWEEN_AUTOSUGGEST_REQUESTS_MSEC);
  }, [router?.query]);

  const onSuggestionsCleared = useCallback(() => {
    setSuggestions([]);
  }, []);

  const handleKeyboardNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>, onSubmit: () => void) => {
      if (suggestions.length && event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedItemIndex(
          (prevIndex: number) => (prevIndex + 1) % suggestions.length
        );
      } else if (suggestions.length && event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedItemIndex(
          (prevIndex: number) =>
            (prevIndex - 1 + suggestions.length) % suggestions.length
        );
      } else {
        setSelectedItemIndex(-1);
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (selectedItemIndex === -1) {
          // Enter pressed on input field
          onSubmit();
          return false;
        }
      }
    },
    [suggestions, selectedItemIndex]
  );

  return {
    suggestions,
    selectedItemIndex,
    setSelectedItemIndex,
    onSuggestionsRequested,
    onSuggestionsCleared,
    handleKeyboardNavigation,
  };
};
